import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Star, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const RATING_CRITERIA = [
  { key: 'good_clothing', label: 'Good Clothing' },
  { key: 'speed', label: 'Speed' },
  { key: 'delivery_quantity', label: 'Delivery Quantity' },
  { key: 'delivery_quality', label: 'Delivery Quality' },
  { key: 'communication', label: 'Communication' },
];

const emptyCriteria = () =>
  RATING_CRITERIA.reduce((acc, c) => ({ ...acc, [c.key]: false }), {});

export const RatingCriteriaDialog = ({ worker, open, onOpenChange, canEdit, onRated }) => {
  const { getAuthHeader } = useAuth();
  const [criteria, setCriteria] = useState(emptyCriteria());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCriteria(worker?.today_criteria ? { ...emptyCriteria(), ...worker.today_criteria } : emptyCriteria());
    }
  }, [open, worker]);

  if (!worker) return null;

  const total = RATING_CRITERIA.filter((c) => criteria[c.key]).length;
  const hasLegacyRating = worker.today_rated && !worker.today_criteria;

  const toggle = (key) => {
    if (!canEdit) return;
    setCriteria((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/admin/ratings/daily/${worker.id}`,
        { rating: total, criteria },
        getAuthHeader()
      );
      toast.success(res.data?.message || `Rated ${worker.name}: ${total} star${total === 1 ? '' : 's'}`);
      onOpenChange(false);
      onRated && onRated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="rating-criteria-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star size={18} className="text-amber-500 fill-amber-500" />
            Rate {worker.name}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500 -mt-2">
          Each criterion met is worth 1 star. Tap the star to award it.
        </p>

        {hasLegacyRating && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="rating-legacy-note">
            Rated {worker.today_rating} ★ today without a criteria breakdown.
            {canEdit ? ' Submitting below will replace it with a criteria-based rating.' : ''}
          </p>
        )}

        <div className="divide-y divide-gray-100" data-testid="rating-criteria-list">
          {RATING_CRITERIA.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => toggle(c.key)}
              disabled={!canEdit}
              className={`w-full flex items-center justify-between py-3 px-1 text-left ${
                canEdit ? 'cursor-pointer hover:bg-amber-50/60' : 'cursor-default'
              } transition-colors rounded-sm`}
              data-testid={`rating-criterion-${c.key}`}
            >
              <span className={`text-sm font-medium ${criteria[c.key] ? 'text-gray-900' : 'text-gray-600'}`}>
                {c.label}
              </span>
              <Star
                size={24}
                className={`transition-colors ${
                  criteria[c.key] ? 'text-amber-500 fill-amber-500' : 'text-gray-300'
                }`}
                data-testid={`rating-criterion-star-${c.key}`}
              />
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3" data-testid="rating-criteria-total">
          <span className="text-sm font-medium text-gray-700">Total rating</span>
          <span className="flex items-center gap-1.5">
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={16} className={s <= total ? 'text-amber-500 fill-amber-500' : 'text-gray-300'} />
              ))}
            </span>
            <span className="text-sm font-bold text-gray-900">{total}/5</span>
          </span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rating-dialog-cancel-btn">
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button
              onClick={submit}
              disabled={total < 1 || submitting}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="rating-dialog-submit-btn"
            >
              {submitting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              {worker.today_rated ? 'Update Rating' : 'Submit Rating'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RatingCriteriaDialog;
